/* ============================================================
   ÉKO '77 — mobile navigation panel (off-canvas)

   Reproduces the reference behaviour (inline script on every page of
   https://eko77-redesign.pages.dev/): the burger slides the panel
   down from the top, the cross and any link inside it slide the panel
   back, and the page behind is locked while it is open.

   THE CONTRACT — already published by assets/x-site-head.js:
     · The header burger fires a CANCELABLE `eko:panel:open` on
       document. This file listens, calls preventDefault() and opens
       the real panel. Without this file the header falls back to
       revealing its own links, so mobile navigation never breaks.
     · Anything else that wants to open the panel can either fire
       `eko:panel:open` or carry [data-x-nav-toggle] — the listener is
       DELEGATED on document, so a header rendered later (or re-rendered
       by the theme editor) is bound without re-running anything.
     · The panel dispatches `eko:panel:opened` / `eko:panel:closed` on
       document, mirroring the `eko:basket:*` contract that
       sections/x-cartbar.liquid established, so any component that
       needs to hide behind the panel can react to it.

   Beyond the reference: Escape closes, focus is trapped while open and
   restored to the burger on close, and a resize to desktop closes the
   panel (the burger that opened it is hidden at >= 1081px, so leaving
   it open would strand a full-screen overlay on a desktop layout).
   ============================================================ */

(function () {
  'use strict';

  if (customElements.get('x-nav-panel')) return;

  var DESKTOP = 1081;
  var LOCK_CLASS = 'x-nav-panel-open';
  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  customElements.define(
    'x-nav-panel',
    class XNavPanel extends HTMLElement {
      constructor() {
        super();
        this.onRequestOpen = this.onRequestOpen.bind(this);
        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onKeydown = this.onKeydown.bind(this);
        this.onResize = this.onResize.bind(this);
      }

      connectedCallback() {
        this.panel = this.querySelector('[data-x-nav-panel]');
        if (!this.panel) return;

        this.closeOnNavigate = this.getAttribute('data-close-on-navigate') !== 'false';
        this.opener = null;

        document.addEventListener('eko:panel:open', this.onRequestOpen);
        document.addEventListener('click', this.onDocumentClick);
        document.addEventListener('keydown', this.onKeydown);
        window.addEventListener('resize', this.onResize, { passive: true });

        /* the theme editor cannot show a full-screen drawer that is
           translated off-screen, so selecting the section opens it */
        if (window.Shopify && window.Shopify.designMode) {
          document.addEventListener('shopify:section:select', function (event) {
            if (event.target.contains(this)) this.open();
          }.bind(this));
          document.addEventListener('shopify:section:deselect', function (event) {
            if (event.target.contains(this)) this.close();
          }.bind(this));
        }
      }

      disconnectedCallback() {
        document.removeEventListener('eko:panel:open', this.onRequestOpen);
        document.removeEventListener('click', this.onDocumentClick);
        document.removeEventListener('keydown', this.onKeydown);
        window.removeEventListener('resize', this.onResize);
        this.unlock();
      }

      get isOpen() {
        return this.panel.classList.contains('is-open');
      }

      /* ---------------- opening ---------------- */

      onRequestOpen(event) {
        /* tell the header we handled it, so it does not also drop its
           own fallback sheet down behind the panel */
        if (event.cancelable) event.preventDefault();
        this.open(event.target);
      }

      onDocumentClick(event) {
        var toggle = event.target.closest('[data-x-nav-toggle]');
        if (toggle) {
          event.preventDefault();
          return this.isOpen ? this.close() : this.open(toggle);
        }

        if (!this.isOpen) return;

        if (event.target.closest('[data-x-nav-close]')) {
          event.preventDefault();
          return this.close();
        }

        /* the reference closes on every link inside the panel, so the
           overlay is never left covering the page it navigated to */
        if (this.closeOnNavigate && event.target.closest('[data-x-nav-panel] a[href]')) {
          this.close();
        }
      }

      /* Where focus goes when the panel closes. The header fires
         `eko:panel:open` on DOCUMENT, so the event carries no element, and a
         click does not reliably focus a <button> (Safari never does, and the
         reference blurs buttons on click outright). Falling back to
         document.activeElement therefore lands on <body> and a keyboard user
         is dumped at the top of the page — so the burger is resolved
         explicitly as the last resort. */
      resolveOpener(opener) {
        if (opener && opener.nodeType === 1 && typeof opener.focus === 'function') return opener;

        var active = document.activeElement;
        if (active && active !== document.body && typeof active.focus === 'function' && !this.contains(active)) {
          return active;
        }

        return document.querySelector('[data-x-head-burger], [data-x-nav-toggle]');
      }

      open(opener) {
        if (this.isOpen) return;

        this.opener = this.resolveOpener(opener);
        this.panel.classList.add('is-open');
        this.panel.setAttribute('aria-hidden', 'false');
        this.lock();
        this.setBurgerState('true');

        var first = this.querySelector('[data-x-nav-close]') || this.querySelector(FOCUSABLE);
        if (first) first.focus();

        document.dispatchEvent(new CustomEvent('eko:panel:opened', { bubbles: true }));
      }

      close() {
        if (!this.isOpen) return;

        this.panel.classList.remove('is-open');
        this.panel.setAttribute('aria-hidden', 'true');
        this.unlock();
        this.setBurgerState('false');

        /* hand focus back to whatever opened the panel — but never to an
           element that is now hidden (closing because the window grew to
           desktop leaves the burger at display:none) */
        if (this.opener && document.contains(this.opener) && this.opener.offsetParent !== null) {
          this.opener.focus();
        }
        this.opener = null;

        document.dispatchEvent(new CustomEvent('eko:panel:closed', { bubbles: true }));
      }

      /* the header owns the burger, so its expanded state is set from
         here — x-site-head.js only does it for its own fallback sheet */
      setBurgerState(value) {
        document.querySelectorAll('[data-x-head-burger], [data-x-nav-toggle]').forEach(function (el) {
          el.setAttribute('aria-expanded', value);
        });
      }

      /* ---------------- keyboard ---------------- */

      onKeydown(event) {
        if (!this.isOpen) return;

        if (event.key === 'Escape') {
          event.preventDefault();
          return this.close();
        }

        if (event.key !== 'Tab') return;

        var items = Array.prototype.filter.call(this.querySelectorAll(FOCUSABLE), function (el) {
          return el.offsetParent !== null;
        });
        if (!items.length) return;

        var first = items[0];
        var last = items[items.length - 1];

        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        } else if (!this.contains(document.activeElement)) {
          event.preventDefault();
          first.focus();
        }
      }

      onResize() {
        if (this.isOpen && window.innerWidth >= DESKTOP) this.close();
      }

      /* ---------------- scroll lock ---------------- */

      lock() {
        document.documentElement.classList.add(LOCK_CLASS);
      }

      unlock() {
        document.documentElement.classList.remove(LOCK_CLASS);
      }
    }
  );
})();
